import { TableCell, TableRow, Typography } from '@mui/material';
import { Order } from '../../../../interfaces/Order/Order';
import { TableColumn } from '../../../../components/DataTable/TableColumn';
import { OrderProduct } from '../../../../interfaces/Order/OrderProduct';
import { TextColumn } from '../../../../components/DataTable/TextColumn';
import { PriceColumn } from '../../../../components/DataTable/PriceColumn';
import { DataTable } from '../../../../components/DataTable/DataTable';
import { sharedColors } from '../../../../utils/Style';
import { useLocalizedText } from '../../../../hooks/useLocalizedText';

type AdditionalRowProps = {
  label: string;
  value: string;
};

const AdditionalRow = ({ label, value }: AdditionalRowProps) => {
  return (
    <TableRow sx={{ backgroundColor: sharedColors.white }}>
      <TableCell colSpan={2}>
        <Typography sx={{ fontWeight: 600 }}>{label}</Typography>
      </TableCell>

      <TableCell>{value}</TableCell>
    </TableRow>
  );
};

type OrderBreakdownTableProps = {
  order: Order;
};

export const OrderBreakdownTable = ({ order }: OrderBreakdownTableProps) => {
  const t = useLocalizedText();

  const getColumns = (hasOffer: boolean): TableColumn<OrderProduct>[] => [
    {
      id: 'name',
      name: t('order.table.product'),
      accessor: (row) => (
        <TextColumn
          value={row.name}
          secondary={row.options
            .map((option) => option.optionValueName)
            .join(' / ')}
        />
      ),
    },
    {
      id: 'qty',
      name: t('order.table.quantity'),
      accessor: (row) => <TextColumn value={row.qty.toString()} />,
    },
    {
      id: 'price',
      name: t('order.table.price'),
      accessor: (row) => (
        <PriceColumn
          specialValue={row.lineTotal.raw}
          value={row.lineTotal.raw + row.discount.raw}
          hasOffer={hasOffer && row.discount.raw > 0}
          hasAtCheckoutDiscount={row.discountedAtCheckout}
        />
      ),
    },
  ];

  const additionalRows = [
    order.discount.raw && !Number.isFinite(order.offerID) ? (
      <AdditionalRow
        label={t('order.table.discount')}
        value={`${order.discount.formatted}`}
      />
    ) : null,
    <AdditionalRow
      label={order.shippingMethod}
      value={order.shippingCost.formatted}
    />,
    order.pointUsage ? (
      <AdditionalRow
        label={t('order.table.point_usage')}
        value={t('order.table.points', { points: order.pointUsage })}
      />
    ) : null,
    order.installment ? (
      <AdditionalRow
        label={t('order.table.instalment')}
        value={order.installment.toString()}
      />
    ) : null,
    <AdditionalRow
      label={t('order.table.total')}
      value={order.total.formatted}
    />,
  ];

  return (
    <DataTable
      columns={getColumns(Number.isFinite(order.offerID))}
      totalCount={order.products.length}
      rows={order.products}
      additionalRows={additionalRows}
      hidePagination
    />
  );
};
